import React, { useCallback, useMemo, useState } from 'react'
import {
	Button,
	Checkbox,
	DatePicker,
	Form,
	Input,
	InputNumber,
	Upload,
	message,
} from 'antd'
import handleFormError from 'utils/handleFormError'
import { AttachmentType, Invoice } from 'types'
import moment, { Moment } from 'moment'
import updateInvoice, { UpdateInvoiceBody } from 'api/invoice/updateInvoice'
import { UploadOutlined } from '@ant-design/icons'
import { UploadRequestOption } from 'rc-upload/lib/interface'
import upload, { FileUpload } from 'api/file/upload'
import getFileNameFromURL from 'utils/getFileNameFromURL'
import handleAxiosError from 'utils/handleAxiosError'

const { Item } = Form

interface PropTypes {
	invoice: Invoice
	onSuccess: () => void
}

interface FormValues {
	dueDate: Moment
	deactivation: boolean
	createdAt: Moment
	amount: string
	number: string
	url: string | undefined
	tdsEnabled: boolean
}

const UpdateInvoiceForm: React.FC<PropTypes> = ({ invoice, onSuccess }) => {
	const invoicePaid = useMemo(() => invoice.isPaid, [invoice.isPaid])

	const [form] = Form.useForm<FormValues>()

	const [loading, setLoading] = useState<boolean>(false)

	const createdDate = Form.useWatch('createdAt', form)
	/**
	 * File Upload Implementation
	 */

	const [sampleAttachment, setSampleAttachment] =
		useState<AttachmentType | null>(
			invoice.url
				? {
						link: invoice.url,
						fileName: getFileNameFromURL(invoice.url),
				  }
				: null
		)
	const [uploadError, setUploadError] = useState(false)

	const uploadFile = async (options: UploadRequestOption<FileUpload>) => {
		try {
			const { url } = await upload(options)
			const { file } = options as any
			setSampleAttachment({
				link: url,
				fileName: file?.name || getFileNameFromURL(url),
			})
		} catch (error: any) {
			handleAxiosError(error)
		}
	}

	const beforeUpload = (file: File) => {
		const isPDF = file.type === 'application/pdf'
		if (!isPDF) {
			message.error(`Only PDF file accepted`)
			setUploadError(true)
		}
		setUploadError(false)
		return isPDF || Upload.LIST_IGNORE
	}

	const handleUploadRemove = useCallback(() => {
		setSampleAttachment(null)
		setTimeout(() => {
			form.setFieldsValue({
				url: undefined,
			})
		}, 0)
	}, [form])

	const onFinish = async (values: FormValues) => {
		if (sampleAttachment === null) {
			message.error('Please upload a PDF file.')
			return
		}

		const body: UpdateInvoiceBody = {
			dueDate: values.dueDate.toJSON(),
			deactivation: values.deactivation,
			createdAt: values.createdAt.toJSON(),
			amount: Number(values.amount),
			number: values.number,
			url: sampleAttachment.link,
			tdsEnabled: values.tdsEnabled,
		}

		const params = {
			id: invoice._id,
			body,
		}

		try {
			setLoading(true)
			await updateInvoice(params)
			setLoading(false)
			message.success('Invoice Successfully Updated.')
			setLoading(false)
			onSuccess()
		} catch (error: any) {
			setLoading(false)
		}
	}

	const handleFiledChange = () => {
		const dueAt = form.getFieldsValue().dueDate
		const created = form.getFieldsValue().createdAt
		if (dueAt && created) {
			if (dueAt.isBefore(created)) {
				form.setFieldsValue({ dueDate: undefined })
			}
		}
	}

	const calculateInvoiceAmount = useCallback(() => {
		const totalAmount = invoice.amount + (invoice.tdsAmount ?? 0)

		const totalTaxPercentage =
			invoice.client.taxation?.reduce(
				(acc, tax) => acc + Number(tax.percentage),
				0
			) ?? 0

		const amountWithoutTax = totalAmount / ((100 + totalTaxPercentage) / 100)

		return amountWithoutTax
	}, [invoice])

	return (
		<Form
			form={form}
			name='UpdateInvoiceForm'
			labelCol={{ span: 20 }}
			wrapperCol={{ span: 20 }}
			onFinish={onFinish}
			onFinishFailed={handleFormError}
			onFieldsChange={handleFiledChange}
			autoComplete='off'
			layout='vertical'
			initialValues={{
				dueDate: invoice.dueDate ? moment(invoice.dueDate) : null,
				deactivation: !!invoice.shouldDeactivateAccount,
				amount: calculateInvoiceAmount(),
				url: sampleAttachment?.link ?? invoice.url,
				number: invoice.number,
				tdsEnabled: invoice.tdsEnabled,
				createdAt: moment(invoice.createdAt),
			}}
		>
			<Item
				label='Amount'
				name='amount'
				rules={[{ required: true, message: 'Please enter amount!' }]}
			>
				<InputNumber style={{ width: '100%' }} min={1} disabled={invoicePaid} />
			</Item>

			<Item
				label='Upload File'
				name='url'
				rules={[{ required: true, message: 'Please upload a pdf file' }]}
			>
				<Upload
					maxCount={1}
					onRemove={handleUploadRemove}
					beforeUpload={beforeUpload}
					showUploadList={!uploadError}
					multiple={false}
					customRequest={uploadFile}
					defaultFileList={[
						...(sampleAttachment !== null
							? [
									{
										uid: '1',
										name:
											sampleAttachment.fileName ||
											getFileNameFromURL(sampleAttachment.link),
										url: sampleAttachment.link,
									},
							  ]
							: []),
					]}
					accept='APPLICATION/PDF'
				>
					<Button icon={<UploadOutlined />}>Upload a PDF File</Button>
				</Upload>
			</Item>

			<Item
				label='Invoice Number'
				name='number'
				rules={[{ required: true, message: 'Please enter invoice number!' }]}
			>
				<Input disabled={invoicePaid} />
			</Item>

			<Item
				label='Creation Date'
				name='createdAt'
				rules={[{ required: true, message: 'Please enter invoice creation date!' }]}
			>
				<DatePicker
					style={{ width: '100%' }}
					format='DD-MM-YYYY'
					disabled={invoicePaid}
				/>
			</Item>

			<Item
				label='Due Date'
				name='dueDate'
				rules={[{ required: true, message: 'Please enter invoice due date!' }]}
			>
				<DatePicker
					style={{ width: '100%' }}
					disabledDate={current => current && current.isBefore(createdDate, 'day')}
					format='DD-MM-YYYY'
					disabled={invoicePaid}
				/>
			</Item>

			<Item
				label='Deactivation'
				name='deactivation'
				valuePropName='checked'
				rules={[{ required: true, message: 'Deactivation value is required!' }]}
			>
				<Checkbox disabled={invoicePaid}>
					Deactivate the account after due date has passed
				</Checkbox>
			</Item>

			<Item
				label='TDS'
				name='tdsEnabled'
				valuePropName='checked'
				rules={[{ required: true, message: 'TDS cannot be undefined!' }]}
			>
				<Checkbox disabled={invoicePaid}>TDS Applicable</Checkbox>
			</Item>

			<Item>
				<Button type='primary' htmlType='submit' loading={loading}>
					Update Invoice
				</Button>
			</Item>
		</Form>
	)
}

export default UpdateInvoiceForm
